import { NavMenuSection } from '@containers/Layout/NavMenu/NavMenuSection';
import { Drawer, Slide, useMediaQuery } from '@mui/material';
import { UserContext } from '@providers/userContextProvider';
import {
  APP_BAR_HEIGHT,
  Box,
  NavCollapse,
  navTransitionDuration,
  navTransitionEasing,
  useTheme,
} from 'hephaestus/web';
import { useContext } from 'react';
import { useLocation } from 'react-router';
import { useMenuMap } from '../../../hooks/useMenuMap';

export type NavMenuProp = {
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
  displayMenuBar: boolean;
};

export const NavMenu = ({
  mobileOpen,
  setMobileOpen,
  displayMenuBar,
}: NavMenuProp) => {
  const { closed, setClosed } = useContext(UserContext);
  const { defaultSections, settingsSections, firstAccessibleMenuPath } =
    useMenuMap();
  const theme = useTheme();
  const isDesktop = useMediaQuery(`(min-width:${theme.breakpoint.lg})`, {
    noSsr: true,
  });
  const railCollapsed = closed && !mobileOpen;
  const location = useLocation();
  const showSettings = location.pathname.includes('/settings');
  const navBackground =
    theme.component?.navSection?.backgroundColor ?? theme.color.background;

  const closeDrawer = () => {
    setMobileOpen(false);
  };

  return (
    <>
      <Drawer
        open={mobileOpen}
        onClose={closeDrawer}
        sx={(t) => ({
          display: {
            xs: 'block',
            sm: 'block',
            md: 'block',
            lg: 'none',
          },
          zIndex: t.zIndex.drawer + 2,
          '& .MuiDrawer-paper': {
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            height: '100%',
            backgroundColor: navBackground,
          },
        })}
      >
        <NavMenuSection
          defaultSections={defaultSections}
          settingsSections={settingsSections}
          firstAccessibleMenuPath={firstAccessibleMenuPath}
          showSettings={showSettings}
          pathname={location.pathname}
          onNavigate={closeDrawer}
        />
      </Drawer>
      <Slide
        direction='right'
        timeout={navTransitionDuration}
        easing={{
          enter: navTransitionEasing,
          exit: navTransitionEasing,
        }}
        in={isDesktop}
        appear={false}
        mountOnEnter
        unmountOnExit
      >
        <Box
          sx={{
            position: 'fixed',
            top: displayMenuBar ? APP_BAR_HEIGHT : 0,
            bottom: 0,
            left: 0,
            overflow: 'visible',
          }}
        >
          <Drawer
            variant='permanent'
            sx={{
              height: '100%',
              '& .MuiDrawer-paper': {
                position: 'relative',
                backgroundColor: 'unset',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                height: '100%',
              },
            }}
            open
          >
            <NavMenuSection
              defaultSections={defaultSections}
              settingsSections={settingsSections}
              firstAccessibleMenuPath={firstAccessibleMenuPath}
              showSettings={showSettings}
              pathname={location.pathname}
              onNavigate={closeDrawer}
              collapsed={railCollapsed}
            />
            <NavCollapse
              collapsed={railCollapsed}
              onClick={() => setClosed(!closed)}
            />
          </Drawer>
        </Box>
      </Slide>
    </>
  );
};
